import EuroIcon from "@mui/icons-material/Euro";
import LoadingButton from "@mui/lab/LoadingButton";
import { Button, Tooltip } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useCallback, useState } from "react";
import { useRefresh } from "react-admin";
import { useQueryClient } from "react-query";
import { getAuthenticatedBackendClient } from "../api/backend.api.js";
import { reportError } from "../backoffice.utils.js";
import { DialogCloseButton } from "../misc/DialogCloseButton.js";
import { useDialog } from "../hooks/useDialog.js";
import { formatCents } from "../misc/Money.js";

export function ManualPrepaidPaymentButton(props: {
  invoiceId: string;
  paymentAmount: number;
  onClickCallback?: () => void;
}) {
  const { invoiceId, paymentAmount, onClickCallback } = props;
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const { openDialog, dialogProps, closeDialog } = useDialog("manualPrepaidPayment");
  const [isLoading, setIsLoading] = useState(false);
  const usePrepaidCreditsToPayInvoice = useCallback(async () => {
    try {
      const backendClient = await getAuthenticatedBackendClient();
      setIsLoading(true);
      await backendClient.invoice.invoiceRetryPaymentCreate({
        id: invoiceId,
        requestBody: { payment_mode: "PREPAID_CREDITS_ONLY" },
      });
      setIsLoading(false);
      showSuccessToast(`Rechnung erfolgreich bezahlt.`);
      void queryClient.invalidateQueries(["b2cInvoices"]); // refresh backend invoice data
      refresh();
    } catch (error) {
      reportError(`Failed to retry payment for invoice ${invoiceId}`, error);
      showErrorToast(`Die Zahlung konnte nicht ausgeführt werden.`);
    } finally {
      setIsLoading(false);
      closeDialog();
    }
  }, [invoiceId]);

  const _openDialog = (event: any) => {
    onClickCallback?.();
    event.stopPropagation();
    openDialog();
  };

  return (
    <>
      <Tooltip title="Rechnung mit Guthaben des Schülers bezahlen.">
        <Button startIcon={<EuroIcon />} onClick={_openDialog}>
          Bezahlen
        </Button>
      </Tooltip>
      <Dialog {...dialogProps}>
        <DialogTitle>Rechnung bezahlen</DialogTitle>
        <DialogCloseButton onClick={closeDialog} disabled={isLoading} />
        <DialogContent>
          <DialogContentText>
            Möchten Sie die offene Rechnung über <strong>{formatCents(paymentAmount)}</strong> mit dem Guthaben des
            Schülers bezahlen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <LoadingButton variant="contained" loading={isLoading} onClick={usePrepaidCreditsToPayInvoice} autoFocus>
            Jetzt bezahlen
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
